/**
 * Virtual Office (ที่ตั้งสำนักงานเพื่อจดทะเบียนบริษัทและจดทะเบียนภาษีมูลค่าเพิ่ม)
 * — axes and service depth content.
 *
 * Deterministic, hand-authored. No AI call at build or request time.
 * Content rules: no service prices, no guarantee that any agency will approve
 * a filing, no unverifiable statistics. Official fee/threshold figures are
 * described qualitatively and always paired with "verify with the agency".
 */
import type { WaveAxis } from "@/data/_qa/wave-expander";

export type VoService = {
  slug: string;
  nameTh: string;
  nameEn: string;
  tagline: string;
  /** 50–80 word direct answer for AI answer engines */
  answer: string;
  seoTitle: string;
  seoDesc: string;
  audience: string[];
  scope: string[];
  docs: string[];
  steps: string[];
  cautions: Array<{ title: string; body: string }>;
  compare: Array<{ option: string; fit: string; watch: string }>;
  related: string[];
};

/** Core service lines of the virtual-office cluster. */
export const VO_SERVICES: VoService[] = [
  {
    slug: "company-registration-address",
    nameTh: "ที่ตั้งสำนักงานเพื่อจดทะเบียนบริษัท",
    nameEn: "Registered office address for company incorporation",
    tagline: "ใช้เป็นที่ตั้งสำนักงานแห่งใหญ่ตามหนังสือรับรองนิติบุคคล",
    answer:
      "ที่ตั้งสำนักงานสำหรับจดทะเบียนบริษัทคือที่อยู่ที่ระบุเป็นสำนักงานแห่งใหญ่ในคำขอจดทะเบียนต่อกรมพัฒนาธุรกิจการค้า ต้องเป็นสถานที่ที่มีอยู่จริง ระบุเลขที่ ห้อง ชั้น อาคาร ถนน แขวง เขต และรหัสไปรษณีย์ได้ครบ และเจ้าของสถานที่ต้องให้ความยินยอมเป็นลายลักษณ์อักษรพร้อมหลักฐานกรรมสิทธิ์หรือสิทธิการเช่า",
    seoTitle: "ที่ตั้งสำนักงานจดทะเบียนบริษัท เอกสาร ขั้นตอน ข้อควรระวัง",
    seoDesc:
      "ที่ตั้งสำนักงานสำหรับจดทะเบียนบริษัทต้องใช้เอกสารอะไร ขั้นตอนยื่นกรมพัฒนาธุรกิจการค้าเป็นอย่างไร และจุดที่ทำให้คำขอถูกให้แก้ไขบ่อยที่สุด",
    audience: [
      "ผู้ที่กำลังจดทะเบียนบริษัทจำกัดใหม่",
      "ห้างหุ้นส่วนจำกัดที่ต้องการที่ตั้งสำนักงานแยกจากบ้านพัก",
      "ชาวต่างชาติที่ร่วมลงทุนในบริษัทไทย",
      "บริษัทที่ต้องย้ายที่ตั้งสำนักงานแห่งใหญ่",
    ],
    scope: [
      "ตรวจชื่อนิติบุคคลและเตรียมคำขอจดทะเบียนพร้อมวัตถุที่ประสงค์",
      "จัดทำหนังสือยินยอมให้ใช้สถานที่ พร้อมสำเนาหลักฐานกรรมสิทธิ์หรือสัญญาเช่าของผู้ให้ความยินยอม",
      "จัดทำแผนที่แสดงที่ตั้งสังเขปและภาพถ่ายสถานที่ตามที่นายทะเบียนขอเพิ่ม",
      "ยื่นคำขอและติดตามผลจนได้หนังสือรับรองนิติบุคคลและ บอจ. ที่เกี่ยวข้อง",
      "ประสานการจัดทำตราประทับและการเปิดบัญชีธนาคารของนิติบุคคล",
    ],
    docs: [
      "สำเนาบัตรประจำตัวประชาชนหรือหนังสือเดินทางของผู้เริ่มก่อการและกรรมการทุกคน",
      "สำเนาทะเบียนบ้านของกรรมการผู้มีอำนาจ",
      "ที่อยู่สถานที่ตั้งสำนักงานแห่งใหญ่ที่ระบุได้ครบถึงเลขห้องและชั้น",
      "หนังสือยินยอมให้ใช้สถานที่จากเจ้าของกรรมสิทธิ์หรือผู้มีสิทธิให้ใช้",
      "สำเนาโฉนด หนังสือกรรมสิทธิ์ห้องชุด หรือสัญญาเช่าของผู้ให้ความยินยอม",
      "แผนที่แสดงที่ตั้งสังเขปพร้อมจุดสังเกตที่เจ้าหน้าที่เดินทางไปตรวจได้",
    ],
    steps: [
      "ยืนยันว่าสถานที่ที่จะใช้อนุญาตให้ใช้เป็นที่ตั้งสำนักงานของนิติบุคคลได้ตามข้อบังคับอาคารหรือนิติบุคคลอาคารชุด",
      "จองชื่อนิติบุคคลและตรวจว่าไม่ซ้ำหรือคล้ายกับที่มีผู้จดไว้แล้ว",
      "จัดทำหนังสือยินยอมให้ใช้สถานที่และรวบรวมหลักฐานสิทธิของผู้ให้ความยินยอม",
      "จัดทำคำขอจดทะเบียนพร้อมวัตถุที่ประสงค์ที่ครอบคลุมธุรกิจที่จะทำจริง",
      "ยื่นคำขอต่อนายทะเบียนและชำระค่าธรรมเนียมตามอัตราของหน่วยงาน",
      "รับหนังสือรับรองและตรวจว่าที่อยู่ในหนังสือรับรองตรงกับเอกสารทุกฉบับที่จะใช้ต่อ",
    ],
    cautions: [
      {
        title: "ที่อยู่ไม่ครบถึงเลขห้องทำให้เอกสารชั้นต่อไปติดขัด",
        body: "ที่อยู่ที่ระบุไม่ครบทำให้เอกสารของธนาคาร กรมสรรพากร และคู่สัญญาไม่ตรงกัน ควรกำหนดรูปแบบที่อยู่ให้เหมือนกันทุกฉบับตั้งแต่ต้น",
      },
      {
        title: "เจ้าของสถานที่ไม่ได้เป็นผู้มีสิทธิให้ใช้จริง",
        body: "ผู้ให้ความยินยอมต้องเป็นเจ้าของกรรมสิทธิ์หรือผู้มีสิทธิตามสัญญาเช่าที่ให้ช่วงต่อได้ หากไม่ใช่ นายทะเบียนอาจให้แก้ไขเอกสารและเรื่องจะล่าช้า",
      },
      {
        title: "อาคารที่พักอาศัยบางแห่งห้ามใช้เป็นที่ตั้งสำนักงาน",
        body: "คอนโดมิเนียมหลายแห่งมีข้อบังคับห้ามประกอบธุรกิจ ควรขอหนังสือยืนยันจากนิติบุคคลอาคารชุดก่อนใช้ที่อยู่นั้น",
      },
    ],
    compare: [
      {
        option: "ใช้ที่ตั้งสำนักงานแบบบริการ (virtual office)",
        fit: "ธุรกิจที่ยังไม่ต้องมีพื้นที่ทำงานประจำ แต่ต้องมีที่อยู่ที่ตรวจสอบได้และมีผู้รับเอกสาร",
        watch: "ต้องตรวจว่าผู้ให้บริการยินยอมให้ใช้จดทะเบียนภาษีมูลค่าเพิ่มด้วยหรือไม่ และรองรับการตรวจสถานประกอบการได้จริง",
      },
      {
        option: "ใช้บ้านหรือคอนโดของกรรมการ",
        fit: "กิจการเล็กที่ทำงานจากที่พักและไม่มีผู้ติดต่อเข้ามาที่สถานที่",
        watch: "ที่อยู่ส่วนตัวจะปรากฏในเอกสารสาธารณะ และอาคารชุดบางแห่งไม่อนุญาต",
      },
      {
        option: "เช่าพื้นที่สำนักงานจริง",
        fit: "ธุรกิจที่มีพนักงานประจำหรือมีลูกค้าเข้าพบที่สำนักงาน",
        watch: "มีภาระผูกพันระยะยาวและงานจัดการพื้นที่เพิ่ม ควรประเมินให้ตรงกับแผนกำลังคน",
      },
    ],
    related: ["vat-registration-address", "post-incorporation-compliance", "office-support-services"],
  },
  {
    slug: "vat-registration-address",
    nameTh: "ที่ตั้งสถานประกอบการเพื่อจดทะเบียนภาษีมูลค่าเพิ่ม",
    nameEn: "Place of business for VAT registration",
    tagline: "รองรับการยื่น ภ.พ.01 และการตรวจสถานประกอบการของเจ้าหน้าที่",
    answer:
      "การจดทะเบียนภาษีมูลค่าเพิ่มใช้ที่ตั้งสถานประกอบการที่เจ้าหน้าที่กรมสรรพากรตรวจสอบได้จริง ต้องมีหลักฐานสิทธิใช้สถานที่ ป้ายแสดงชื่อผู้ประกอบการที่มองเห็นจากภายนอก แผนที่ตั้ง ภาพถ่ายสถานที่ และผู้รับการติดต่อในเวลาทำการ ที่อยู่ที่ใช้เพียงรับจดหมายโดยไม่มีผู้รับการตรวจมักถูกขอเอกสารเพิ่มหรือไม่ผ่านการพิจารณา",
    seoTitle: "จด VAT ใช้ที่ตั้งสถานประกอบการแบบไหนได้ เอกสารและการตรวจ",
    seoDesc:
      "เงื่อนไขที่ตั้งสถานประกอบการสำหรับยื่น ภ.พ.01 เอกสารที่ต้องเตรียม การติดป้ายชื่อ การตรวจสภาพกิจการ และสาเหตุที่คำขอถูกให้แก้ไข",
    audience: [
      "ผู้ประกอบการที่รายรับถึงเกณฑ์ต้องจดทะเบียนภาษีมูลค่าเพิ่ม",
      "ผู้ที่ต้องการจดทะเบียนก่อนถึงเกณฑ์เพื่อออกใบกำกับภาษี",
      "ผู้นำเข้าส่งออกที่คู่ค้าต้องการใบกำกับภาษี",
      "บริษัทที่ย้ายสถานประกอบการและต้องแจ้งเปลี่ยนแปลง",
    ],
    scope: [
      "ประเมินความพร้อมของสถานที่ก่อนยื่นคำขอ ทั้งป้ายชื่อ พื้นที่รับรอง และผู้รับการติดต่อ",
      "จัดเตรียมชุดเอกสารประกอบคำขอจดทะเบียนภาษีมูลค่าเพิ่มให้ครบก่อนยื่น",
      "จัดทำแผนที่ตั้ง ภาพถ่ายภายนอกภายใน และผังการใช้พื้นที่ตามที่เจ้าหน้าที่ขอ",
      "ประสานการนัดหมายและอยู่รับการตรวจสภาพกิจการในวันที่เจ้าหน้าที่เข้าตรวจ",
      "ติดตามผลและดำเนินการชั้นแจ้งเปลี่ยนแปลงหากมีการแก้ไขข้อมูล",
    ],
    docs: [
      "หนังสือรับรองนิติบุคคลฉบับล่าสุดและวัตถุที่ประสงค์",
      "บัตรประจำตัวและทะเบียนบ้านของกรรมการผู้มีอำนาจ",
      "สัญญาเช่าหรือหนังสือยินยอมให้ใช้สถานที่ พร้อมหลักฐานสิทธิของผู้ให้ใช้",
      "แผนที่แสดงที่ตั้งสถานประกอบการพร้อมจุดสังเกต",
      "ภาพถ่ายสถานประกอบการที่เห็นป้ายชื่อผู้ประกอบการจากภายนอก และภาพภายในพื้นที่ที่ใช้งาน",
      "หนังสือมอบอำนาจพร้อมบัตรประจำตัวของผู้รับมอบ หากไม่ยื่นด้วยตนเอง",
    ],
    steps: [
      "ตรวจว่ากิจการเข้าเกณฑ์ที่ต้องจดทะเบียนแล้วหรือเลือกจดก่อนถึงเกณฑ์",
      "จัดสถานที่ให้พร้อมรับการตรวจ ติดป้ายแสดงชื่อผู้ประกอบการให้เห็นชัดจากภายนอก",
      "รวบรวมเอกสารประกอบคำขอและตรวจว่าที่อยู่ตรงกับหนังสือรับรองทุกตัวอักษร",
      "ยื่นคำขอจดทะเบียนภาษีมูลค่าเพิ่มต่อสำนักงานสรรพากรพื้นที่ที่สถานประกอบการตั้งอยู่ หรือผ่านช่องทางออนไลน์ที่กรมสรรพากรกำหนด",
      "รับการตรวจสภาพกิจการโดยมีผู้ให้ข้อมูลอยู่ที่สถานที่ในเวลาทำการ",
      "เมื่อได้รับใบทะเบียนภาษีมูลค่าเพิ่ม ให้ติดแสดงไว้ที่สถานประกอบการและเริ่มออกใบกำกับภาษีตามรูปแบบที่กฎหมายกำหนด",
    ],
    cautions: [
      {
        title: "ไม่มีผู้รับการตรวจในวันที่เจ้าหน้าที่เข้าตรวจ",
        body: "การตรวจสภาพกิจการมักไม่แจ้งเวลาแน่นอน หากไม่มีผู้ให้ข้อมูลอยู่ที่สถานที่ เรื่องจะถูกเลื่อนหรือถูกขอเอกสารเพิ่ม ควรตกลงกับผู้ให้บริการสถานที่ไว้ล่วงหน้าว่าใครเป็นผู้รับการติดต่อ",
      },
      {
        title: "ไม่ได้ติดป้ายแสดงชื่อผู้ประกอบการ",
        body: "ป้ายชื่อที่มองเห็นจากภายนอกเป็นสิ่งที่เจ้าหน้าที่ตรวจเป็นลำดับแรก ควรติดตั้งให้ถูกตำแหน่งและถ่ายภาพเก็บไว้เป็นหลักฐาน",
      },
      {
        title: "ที่อยู่ในเอกสารไม่ตรงกันแม้เพียงเลขห้อง",
        body: "หนังสือรับรอง สัญญาเช่า และคำขอต้องใช้ที่อยู่รูปแบบเดียวกัน ความต่างเล็กน้อยทำให้ต้องยื่นแก้ไขและเสียเวลารอบใหม่",
      },
      {
        title: "ใช้ที่อยู่ที่มีผู้ประกอบการจำนวนมากโดยไม่แยกพื้นที่ใช้งาน",
        body: "หากหลายกิจการใช้ที่อยู่เดียวกันโดยไม่มีการแบ่งพื้นที่หรือหลักฐานการใช้งานที่ชัดเจน เจ้าหน้าที่อาจขอหลักฐานเพิ่มเติมเพื่อยืนยันว่ามีการประกอบกิจการจริง",
      },
    ],
    compare: [
      {
        option: "จดทะเบียนภาษีมูลค่าเพิ่มที่ที่ตั้งสำนักงานแบบบริการ",
        fit: "กิจการที่ทำงานจากระยะไกลแต่ต้องออกใบกำกับภาษี และผู้ให้บริการรองรับการตรวจสภาพกิจการ",
        watch: "ต้องยืนยันก่อนทำสัญญาว่าผู้ให้บริการยินยอมให้จดภาษีมูลค่าเพิ่ม ให้ติดป้ายชื่อ และมีผู้รับการตรวจ",
      },
      {
        option: "จดที่บ้านหรือที่พักของกรรมการ",
        fit: "กิจการขนาดเล็กที่มีพื้นที่ทำงานจริงที่ที่พักและติดป้ายชื่อได้",
        watch: "ต้องดูข้อบังคับอาคารชุดและการเปิดเผยที่อยู่ส่วนตัวต่อคู่ค้า",
      },
      {
        option: "จดที่พื้นที่เช่าสำนักงานจริง",
        fit: "กิจการที่มีพนักงานหรือสินค้าอยู่ที่สถานที่",
        watch: "ค่าใช้จ่ายคงที่สูงกว่า และต้องมีสัญญาเช่าที่ระบุการใช้เพื่อประกอบกิจการ",
      },
    ],
    related: ["company-registration-address", "post-incorporation-compliance", "foreign-owned-setup"],
  },
  {
    slug: "office-support-services",
    nameTh: "บริการรับเอกสารและงานสนับสนุนสำนักงาน",
    nameEn: "Mail handling and office support",
    tagline: "รับจดหมายราชการ แจ้งเตือน และส่งต่อเอกสารสำคัญตามกำหนดเวลา",
    answer:
      "งานสนับสนุนสำนักงานสำหรับที่ตั้งแบบบริการครอบคลุมการรับจดหมายและหนังสือราชการ แจ้งผู้รับเมื่อมีเอกสารเข้า สแกนส่งต่อ เก็บต้นฉบับให้รับได้ และบันทึกวันที่รับเอกสารไว้เป็นหลักฐาน จุดสำคัญคือหนังสือจากศาลหรือหน่วยงานที่มีกำหนดเวลาตอบ ต้องส่งต่อถึงผู้มีอำนาจโดยเร็วที่สุด",
    seoTitle: "บริการรับจดหมายที่ตั้งสำนักงาน แจ้งเตือนเอกสารราชการ",
    seoDesc:
      "ที่ตั้งสำนักงานแบบบริการรับเอกสารอะไรได้ ส่งต่ออย่างไร บันทึกวันรับเอกสารเพื่ออะไร และสิ่งที่ต้องตกลงให้ชัดในสัญญาก่อนเริ่มใช้",
    audience: [
      "กิจการที่ทีมงานทำงานจากหลายพื้นที่",
      "บริษัทต่างชาติที่กรรมการอยู่ต่างประเทศ",
      "กิจการที่ต้องแยกที่อยู่ธุรกิจออกจากที่พัก",
    ],
    scope: [
      "รับจดหมาย พัสดุเอกสาร และหนังสือจากหน่วยงานราชการในเวลาทำการ",
      "แจ้งเตือนผู้ติดต่อที่กำหนดไว้เมื่อมีเอกสารเข้า พร้อมระบุผู้ส่งและวันที่รับ",
      "สแกนส่งต่อเอกสารตามช่องทางที่ตกลง และเก็บต้นฉบับไว้ให้มารับหรือจัดส่งต่อ",
      "จัดทำบันทึกการรับส่งเอกสารเพื่อใช้อ้างอิงกรณีมีข้อโต้แย้งเรื่องกำหนดเวลา",
      "รับการติดต่อจากเจ้าหน้าที่หน่วยงานและประสานกับผู้มีอำนาจของกิจการ",
    ],
    docs: [
      "หนังสือรับรองนิติบุคคลและบัตรประจำตัวของกรรมการผู้มีอำนาจ",
      "รายชื่อผู้ติดต่อที่ได้รับอนุญาตให้รับแจ้งและรับเอกสาร",
      "หนังสือมอบอำนาจให้รับเอกสารแทน หากมอบให้บุคคลอื่นมารับ",
    ],
    steps: [
      "กำหนดผู้ติดต่อหลักและช่องทางแจ้งเตือนที่ตรวจทุกวันทำการ",
      "ตกลงวิธีจัดการเอกสารแต่ละประเภท โดยเฉพาะหนังสือที่มีกำหนดเวลาตอบ",
      "เริ่มใช้ที่อยู่ในเอกสารธุรกิจโดยใช้รูปแบบที่อยู่เดียวกันทุกฉบับ",
      "ตรวจบันทึกการรับเอกสารเป็นรายเดือนเพื่อยืนยันว่าไม่มีเอกสารคงค้าง",
    ],
    cautions: [
      {
        title: "หนังสือที่มีกำหนดเวลาต้องส่งต่อทันที",
        body: "หมายศาล หนังสือแจ้งประเมินภาษี และหนังสือเชิญพบเจ้าหน้าที่มีกำหนดเวลาตอบ ความล่าช้าในการส่งต่ออาจทำให้เสียสิทธิ ควรระบุในสัญญาว่าเอกสารประเภทนี้แจ้งภายในกี่ชั่วโมงทำการ",
      },
      {
        title: "ผู้รับเอกสารต้องเป็นผู้ได้รับมอบอำนาจ",
        body: "การให้บุคคลที่ไม่มีอำนาจรับเอกสารแทนอาจทำให้เกิดข้อโต้แย้งเรื่องการรับทราบ ควรทำรายชื่อผู้มีสิทธิรับเอกสารไว้เป็นลายลักษณ์อักษร",
      },
    ],
    compare: [
      {
        option: "ให้ผู้ให้บริการสแกนส่งต่อทุกฉบับ",
        fit: "กิจการที่ต้องการเห็นเอกสารทันทีและตัดสินใจเร็ว",
        watch: "ต้องตกลงเรื่องการรักษาความลับและการเก็บไฟล์",
      },
      {
        option: "เก็บรวมแล้วรับเดือนละครั้ง",
        fit: "กิจการที่ได้รับเอกสารน้อยและไม่มีเรื่องที่มีกำหนดเวลา",
        watch: "ไม่เหมาะกับกิจการที่อาจได้รับหนังสือราชการ เพราะเสี่ยงพลาดกำหนดเวลา",
      },
    ],
    related: ["company-registration-address", "vat-registration-address", "post-incorporation-compliance"],
  },
  {
    slug: "post-incorporation-compliance",
    nameTh: "งานหลังจดทะเบียนและการรักษาสถานะนิติบุคคล",
    nameEn: "Post-incorporation compliance",
    tagline: "บัญชี ภาษี งบการเงิน และการแจ้งเปลี่ยนแปลงให้ครบกำหนด",
    answer:
      "หลังจดทะเบียนบริษัทและจดทะเบียนภาษีมูลค่าเพิ่ม กิจการมีหน้าที่ต่อเนื่องคือจัดทำบัญชีตามกฎหมาย ยื่นแบบภาษีรายเดือนและรายปี จัดประชุมผู้ถือหุ้นและนำส่งงบการเงินต่อกรมพัฒนาธุรกิจการค้า และแจ้งเปลี่ยนแปลงทุกครั้งที่ที่ตั้ง กรรมการ หรือวัตถุที่ประสงค์เปลี่ยน การพลาดกำหนดเวลาทำให้มีเบี้ยปรับตามอัตราของหน่วยงาน",
    seoTitle: "หน้าที่หลังจดบริษัทและจด VAT ต้องทำอะไรต่อบ้าง",
    seoDesc:
      "รายการหน้าที่ต่อเนื่องหลังจดทะเบียนบริษัทและภาษีมูลค่าเพิ่ม ทั้งบัญชี ภาษีรายเดือน งบการเงิน และการแจ้งเปลี่ยนแปลงข้อมูลนิติบุคคล",
    audience: [
      "บริษัทที่จดทะเบียนใหม่ในปีนี้",
      "กิจการที่เพิ่งเริ่มอยู่ในระบบภาษีมูลค่าเพิ่ม",
      "กิจการที่ยังไม่มีผู้ทำบัญชีประจำ",
    ],
    scope: [
      "วางระบบเอกสารรับจ่ายและใบกำกับภาษีให้ถูกรูปแบบตั้งแต่เดือนแรก",
      "จัดทำบัญชีและปิดงบตามรอบบัญชี พร้อมประสานผู้สอบบัญชี",
      "ยื่นแบบภาษีรายเดือนและรายปีตามกำหนดเวลาของกรมสรรพากร",
      "จัดประชุมผู้ถือหุ้นและนำส่งงบการเงินต่อกรมพัฒนาธุรกิจการค้า",
      "ยื่นคำขอจดทะเบียนแก้ไขเปลี่ยนแปลงเมื่อข้อมูลนิติบุคคลเปลี่ยน",
    ],
    docs: [
      "เอกสารรับจ่ายและใบกำกับภาษีซื้อขายของแต่ละเดือน",
      "รายการเดินบัญชีธนาคารของนิติบุคคล",
      "ทะเบียนทรัพย์สินและสัญญาที่มีผลต่อรายได้รายจ่าย",
      "หนังสือรับรองนิติบุคคลและบัญชีรายชื่อผู้ถือหุ้นฉบับล่าสุด",
    ],
    steps: [
      "กำหนดรอบบัญชีและวันปิดงบให้ชัดตั้งแต่เริ่มกิจการ",
      "จัดเก็บเอกสารรายเดือนให้ครบก่อนกำหนดยื่นแบบ",
      "ยื่นแบบภาษีรายเดือนภายในกำหนดของแต่ละแบบ",
      "ปิดงบประจำปี ให้ผู้สอบบัญชีตรวจ และจัดประชุมอนุมัติงบการเงิน",
      "นำส่งงบการเงินและยื่นแบบภาษีเงินได้นิติบุคคลตามกำหนด",
      "ทบทวนข้อมูลที่จดทะเบียนไว้ทุกปีว่าตรงกับความจริงหรือไม่",
    ],
    cautions: [
      {
        title: "ย้ายที่ตั้งแล้วไม่แจ้งเปลี่ยนแปลง",
        body: "การเปลี่ยนที่ตั้งสำนักงานหรือสถานประกอบการต้องยื่นแจ้งต่อหน่วยงานที่เกี่ยวข้องภายในกำหนด หากไม่แจ้ง หนังสือราชการจะส่งไปที่อยู่เดิมและอาจทำให้พลาดกำหนดเวลาสำคัญ",
      },
      {
        title: "ออกใบกำกับภาษีไม่ถูกรูปแบบ",
        body: "ใบกำกับภาษีต้องมีรายการครบตามที่กฎหมายกำหนด รวมถึงที่อยู่สถานประกอบการตามที่จดทะเบียนไว้ ความผิดพลาดซ้ำ ๆ ทำให้ผู้ซื้อใช้ภาษีซื้อไม่ได้และเกิดข้อโต้แย้งภายหลัง",
      },
      {
        title: "ไม่ส่งงบการเงินตามกำหนด",
        body: "การนำส่งงบการเงินล่าช้ามีเบี้ยปรับตามอัตราของหน่วยงาน และกระทบความน่าเชื่อถือเมื่อยื่นขอสินเชื่อหรือเข้าร่วมประมูลงาน",
      },
    ],
    compare: [
      {
        option: "ใช้สำนักงานบัญชีภายนอก",
        fit: "กิจการที่ปริมาณเอกสารยังไม่มากพอจะจ้างพนักงานบัญชีประจำ",
        watch: "ต้องตกลงขอบเขตงานให้ชัดว่ารวมการยื่นแบบและการปิดงบหรือไม่",
      },
      {
        option: "จ้างพนักงานบัญชีประจำ",
        fit: "กิจการที่มีรายการเดินบัญชีจำนวนมากทุกวัน",
        watch: "ต้องมีผู้ทบทวนงานและผู้สอบบัญชีภายนอกตามกฎหมายอยู่แล้ว",
      },
    ],
    related: ["vat-registration-address", "company-registration-address", "foreign-owned-setup"],
  },
  {
    slug: "foreign-owned-setup",
    nameTh: "การตั้งกิจการที่มีผู้ถือหุ้นต่างชาติ",
    nameEn: "Foreign-owned company setup",
    tagline: "โครงสร้างผู้ถือหุ้น ใบอนุญาต และเอกสารของผู้ถือหุ้นต่างชาติ",
    answer:
      "กิจการที่มีผู้ถือหุ้นต่างชาติต้องตรวจสองชั้นก่อนจดทะเบียน ชั้นแรกคือธุรกิจที่จะทำอยู่ในบัญชีที่กฎหมายว่าด้วยการประกอบธุรกิจของคนต่างด้าวจำกัดไว้หรือไม่ ชั้นที่สองคือเอกสารของผู้ถือหุ้นและกรรมการต่างชาติที่ออกจากต่างประเทศต้องรับรองในรูปแบบที่หน่วยงานไทยยอมรับ และต้องมีคำแปลรับรอง",
    seoTitle: "จดบริษัทผู้ถือหุ้นต่างชาติ ตรวจอะไรก่อน เอกสารอะไรต้องรับรอง",
    seoDesc:
      "ขั้นตอนตรวจข้อจำกัดธุรกิจของคนต่างด้าว เอกสารผู้ถือหุ้นและกรรมการต่างชาติที่ต้องรับรองและแปล และจุดที่มักทำให้คำขอล่าช้า",
    audience: [
      "นักลงทุนต่างชาติที่จะตั้งบริษัทในไทย",
      "บริษัทต่างประเทศที่จะตั้งบริษัทลูกในไทย",
      "คนไทยที่ร่วมทุนกับผู้ถือหุ้นต่างชาติ",
    ],
    scope: [
      "ตรวจว่าธุรกิจที่จะทำต้องขอใบอนุญาตประกอบธุรกิจของคนต่างด้าวหรือมีเงื่อนไขเฉพาะหรือไม่",
      "ออกแบบโครงสร้างผู้ถือหุ้นและอำนาจกรรมการให้สอดคล้องกับข้อกำหนดและเป้าหมายทางธุรกิจ",
      "จัดการรับรองเอกสารของผู้ถือหุ้นนิติบุคคลต่างประเทศและคำแปลรับรอง",
      "ประสานงานเอกสารกับสถานทูตหรือกรมการกงสุลเมื่อหน่วยงานปลายทางกำหนด",
      "เตรียมเอกสารต่อเนื่องเรื่องใบอนุญาตทำงานและวีซ่าของผู้บริหารต่างชาติ",
    ],
    docs: [
      "หนังสือเดินทางของผู้ถือหุ้นและกรรมการต่างชาติทุกคน",
      "หนังสือรับรองการจัดตั้งของผู้ถือหุ้นนิติบุคคลต่างประเทศที่รับรองตามรูปแบบที่หน่วยงานไทยยอมรับ",
      "หนังสือมอบอำนาจของผู้ถือหุ้นนิติบุคคลพร้อมคำแปลรับรอง",
      "หลักฐานที่มาของเงินลงทุนตามที่หน่วยงานหรือธนาคารขอ",
      "หนังสือยินยอมให้ใช้สถานที่และหลักฐานสิทธิของผู้ให้ใช้",
    ],
    steps: [
      "ระบุกิจกรรมทางธุรกิจที่จะทำจริงให้ชัด แล้วตรวจข้อจำกัดของกฎหมายว่าด้วยการประกอบธุรกิจของคนต่างด้าว",
      "วางโครงสร้างผู้ถือหุ้นและอำนาจลงนามให้สอดคล้องกับข้อจำกัดและการบริหารจริง",
      "จัดเตรียมและรับรองเอกสารต่างประเทศพร้อมคำแปลก่อนยื่นคำขอ",
      "จดทะเบียนบริษัทและดำเนินการเรื่องที่ตั้งสำนักงานให้ครบเอกสาร",
      "จดทะเบียนภาษีมูลค่าเพิ่มหากเข้าเกณฑ์หรือจำเป็นต่อการออกใบกำกับภาษี",
      "ดำเนินเรื่องใบอนุญาตทำงานและวีซ่าของผู้บริหารต่างชาติเป็นลำดับถัดไป",
    ],
    cautions: [
      {
        title: "เอกสารต่างประเทศยังไม่ได้รับรองในรูปแบบที่ยอมรับ",
        body: "เอกสารที่ออกจากต่างประเทศมักต้องผ่านการรับรองตามที่หน่วยงานปลายทางกำหนดและต้องมีคำแปล การยื่นก่อนรับรองครบทำให้ถูกให้แก้ไขและเสียเวลารอบใหม่",
      },
      {
        title: "โครงสร้างผู้ถือหุ้นที่ทำขึ้นเพื่อเลี่ยงข้อจำกัด",
        body: "การใช้ผู้ถือหุ้นไทยที่ไม่ได้ลงทุนจริงเพื่อเลี่ยงข้อจำกัดตามกฎหมายมีความเสี่ยงทางกฎหมายสูง ควรออกแบบโครงสร้างจากข้อเท็จจริงของการลงทุนที่เกิดขึ้นจริง",
      },
      {
        title: "ชื่อในหนังสือเดินทางกับเอกสารอื่นสะกดไม่ตรงกัน",
        body: "ตัวสะกดชื่อที่ต่างกันระหว่างหนังสือเดินทาง หนังสือมอบอำนาจ และคำแปล ทำให้นายทะเบียนขอเอกสารยืนยันเพิ่ม ควรกำหนดการสะกดชื่อให้เหมือนกันทุกฉบับ",
      },
    ],
    compare: [
      {
        option: "บริษัทจำกัดที่มีผู้ถือหุ้นไทยร่วมลงทุนจริง",
        fit: "ธุรกิจที่มีผู้ร่วมทุนไทยและต้องการเริ่มดำเนินการโดยไม่ต้องขอใบอนุญาตเพิ่ม",
        watch: "ต้องมีการลงทุนและบทบาทของผู้ถือหุ้นไทยที่เป็นจริงและตรวจสอบได้",
      },
      {
        option: "ขอใบอนุญาตประกอบธุรกิจของคนต่างด้าว",
        fit: "ธุรกิจที่ต่างชาติต้องการถือหุ้นส่วนใหญ่ในกิจกรรมที่กฎหมายจำกัด",
        watch: "ใช้เวลาพิจารณาและต้องมีเอกสารสนับสนุนครบ ผลเป็นดุลพินิจของหน่วยงาน",
      },
      {
        option: "ตั้งสำนักงานผู้แทนหรือสาขาของบริษัทต่างประเทศ",
        fit: "กิจกรรมที่ไม่ใช่การขายหรือหารายได้ในประเทศ",
        watch: "ขอบเขตกิจกรรมจำกัดเข้มงวด การทำเกินขอบเขตมีผลทางกฎหมาย",
      },
    ],
    related: ["company-registration-address", "vat-registration-address", "post-incorporation-compliance"],
  },
];

export const VO_SERVICE_BY_SLUG: Record<string, VoService> = Object.fromEntries(
  VO_SERVICES.map((s) => [s.slug, s]),
);

/** Primary Q&A topic axis. */
export const VO_TOPIC_AXIS: readonly WaveAxis[] = [
  { slug: "registered-address", th: "ที่ตั้งสำนักงานเพื่อจดทะเบียนบริษัท" },
  { slug: "vat-address", th: "ที่ตั้งสถานประกอบการเพื่อจดทะเบียนภาษีมูลค่าเพิ่ม" },
  { slug: "consent-letter", th: "หนังสือยินยอมให้ใช้สถานที่และหลักฐานสิทธิของผู้ให้ใช้" },
  { slug: "site-inspection", th: "การตรวจสภาพกิจการของเจ้าหน้าที่ ณ สถานประกอบการ" },
  { slug: "signage", th: "การติดป้ายแสดงชื่อผู้ประกอบการที่สถานประกอบการ" },
  { slug: "address-change", th: "การย้ายที่ตั้งและการแจ้งเปลี่ยนแปลงข้อมูลนิติบุคคล" },
  { slug: "mail-handling", th: "การรับจดหมายและหนังสือราชการที่ที่ตั้งสำนักงาน" },
  { slug: "condo-rules", th: "การใช้ห้องชุดหรือที่พักอาศัยเป็นที่ตั้งสำนักงาน" },
  { slug: "tax-invoice", th: "การออกใบกำกับภาษีให้ตรงกับที่อยู่ที่จดทะเบียนไว้" },
  { slug: "multi-branch", th: "การจดสถานประกอบการหลายแห่งและสำนักงานสาขา" },
  { slug: "foreign-shareholder", th: "กิจการที่มีผู้ถือหุ้นหรือกรรมการต่างชาติ" },
  { slug: "work-permit-link", th: "ความเชื่อมโยงระหว่างที่ตั้งสำนักงานกับใบอนุญาตทำงาน" },
  { slug: "ecommerce", th: "ธุรกิจออนไลน์และการจดทะเบียนพาณิชย์อิเล็กทรอนิกส์" },
  { slug: "bank-account", th: "การเปิดบัญชีธนาคารของนิติบุคคลโดยใช้ที่อยู่จดทะเบียน" },
  { slug: "contract-terms", th: "สัญญาใช้บริการที่ตั้งสำนักงานและข้อที่ต้องตกลงให้ชัด" },
];

/** Secondary axis: business-district and provincial demand zones. */
export const VO_ZONE_AXIS: readonly WaveAxis[] = [
  { slug: "sathorn", th: "สาทร" },
  { slug: "silom", th: "สีลม" },
  { slug: "asoke", th: "อโศก" },
  { slug: "sukhumvit", th: "สุขุมวิท" },
  { slug: "rama-9", th: "พระราม 9" },
  { slug: "ratchada", th: "รัชดาภิเษก" },
  { slug: "ladprao", th: "ลาดพร้าว" },
  { slug: "bang-na", th: "บางนา" },
  { slug: "chatuchak", th: "จตุจักร" },
  { slug: "nonthaburi", th: "นนทบุรี" },
  { slug: "pathum-thani", th: "ปทุมธานี" },
  { slug: "samut-prakan", th: "สมุทรปราการ" },
  { slug: "chon-buri", th: "ชลบุรี" },
  { slug: "pattaya", th: "พัทยา" },
  { slug: "rayong", th: "ระยอง" },
  { slug: "chiang-mai", th: "เชียงใหม่" },
  { slug: "khon-kaen", th: "ขอนแก่น" },
  { slug: "udon-thani", th: "อุดรธานี" },
  { slug: "nakhon-ratchasima", th: "นครราชสีมา" },
  { slug: "phuket", th: "ภูเก็ต" },
  { slug: "surat-thani", th: "สุราษฎร์ธานี" },
  { slug: "hat-yai", th: "หาดใหญ่" },
];
